import {
  normalizedHourDraftToXhrFactory,
  omitLinkedWorkItemIdsFromUnsupportedCreate,
} from 'factories/hours';
import { getHasLinkedWorkItemsSupport } from 'redux/selectors/accounts';
import type { IState } from 'redux/state';
import type { Hour, HourDraft, SetTimerState } from 'types/hour';
import { calculateDuration, durationFromHHMMSS } from 'utils/durations';
import { dayTools } from 'utils/time';
import { finalizeHourAnalyticsContext } from './analytics-context';

export const toggleTimerOnHour = <
  THour extends Pick<
    Hour<number>,
    'timer_state' | 'timer_started_on' | 'timer_stopped_on' | 'duration'
  >,
>(
  hour: THour
): THour => {
  const setTimerState =
    hour.timer_state != null && ['default', 'stop'].includes(hour.timer_state) ? 'start' : 'stop';

  if (setTimerState === 'start') {
    return {
      ...hour,
      timer_state: setTimerState,
      timer_started_on: dayTools.unix(),
      timer_stopped_on: 0,
      // Make sure timers start on 1 second
      duration: hour.duration.total_seconds > 0 ? hour.duration : durationFromHHMMSS(0, 0, 1),
    };
  }

  return {
    ...hour,
    timer_state: setTimerState,
    timer_stopped_on: dayTools.unix(),
    timer_started_on: 0,
    // We need to merge the duration + duration on timer:
    duration: calculateDuration(hour.duration.total_seconds, hour.timer_started_on),
  };
};

export function getToggleTimerChanges(state: IState, draft: HourDraft) {
  const setTimerState: SetTimerState = ['default', 'stop'].includes(draft.timer_state)
    ? 'start'
    : 'stop';

  // Toggling the timer on an unsaved draft creates the hour first, so it goes through the same
  // create guard as every other save path.
  const saveHourPayload = omitLinkedWorkItemIdsFromUnsupportedCreate(
    normalizedHourDraftToXhrFactory(
      draft,
      finalizeHourAnalyticsContext({ ...draft, timer_state: setTimerState }, state)
    ),
    { isCreate: !draft.id, hasLinkedWorkItemsSupport: getHasLinkedWorkItemsSupport(state) }
  );

  return { setTimerState, saveHourPayload };
}
